
import Vue from "vue";
import Component from "vue-class-component";
import { Prop } from "vue-property-decorator";

import Row from "@/components/layout/Row.vue";
import Column from "@/components/layout/Column.vue";
import Checkbox from "@/components/form/Checkbox.vue";
import Textfield from "@/components/form/Textfield.vue";

import { FormField } from "@/shared/form/FormData";
import {
  DocumentListOption,
  documentListFrom
} from "../models/DocumentListOptions";

/**
 * The document-list editor of the Bauwerksbuch report.
 *
 * Its option set is fixed Bauwerksbuch domain content rather than a
 * configurable list, so the widget lives in the report module instead of among
 * the generic form primitives — it would only belong there once the options
 * were passed in as a prop.
 *
 * It renders the six German options as checkboxes; every option except "Keine"
 * carries a free-text specification that is enabled while the option is
 * checked. Each edit emits the whole list, normalized against the catalog, so
 * the stored content shape stays exactly the one the renderer reads.
 */
@Component<DocumentListField>({
  components: {
    Row,
    Column,
    Checkbox,
    Textfield
  }
})
export default class DocumentListField extends Vue {
  @Prop({
    type: Object,
    default: () => ({ label: "Dokumentenliste", value: [], error: "" })
  })
  protected readonly value!: FormField;

  @Prop({ type: Boolean, default: false })
  protected readonly disabled!: boolean;

  @Prop({ type: Boolean, default: false })
  protected readonly loading!: boolean;

  protected get label() {
    return this.value?.label ?? "";
  }

  protected get entries(): DocumentListOption[] {
    return documentListFrom(this.value?.value);
  }

  protected hasSpecification(entry: DocumentListOption) {
    return entry.specification !== undefined;
  }

  protected check(entry: DocumentListOption, checked: boolean) {
    this.emitEntry({ ...entry, checked: !!checked });
  }

  protected specify(entry: DocumentListOption, specification: string) {
    this.emitEntry({ ...entry, specification: specification ?? "" });
  }

  private emitEntry(changed: DocumentListOption) {
    this.$emit(
      "change",
      this.entries.map(entry => (entry.key === changed.key ? changed : entry))
    );
  }
}
